import * as React from 'react';
import {
  BodyText,
  Card,
  ErrorMessage,
  FlexRow,
  SpaceWrapper,
  Spinner,
  SpinnerSize,
  StickyNavigation,
  SwitchDialogWindows,
} from 'ui-toolkit/index';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { UserProfileContainer } from 'components/userProfile/UserProfileContainer';
import { TaxCertificationForm } from 'components/order/taxCertifications/TaxCertificationForm';
import {
  postTaxCertificationsAsyncAction,
  postTaxCertificationsSlice,
} from 'store/order/taxCertifications/postTaxCertificationSlice';
import { useAppDispatch } from 'store/store';
import { defaultTaxCertificationData, ITaxCertificationData } from 'ui-toolkit/taxCertifications/ITaxCertification';
import { useEffect } from 'react';
import { getCountyForStateAction } from '../../../store/counties';
import { getTaxCertificationAsyncAction } from '../../../store/order/taxCertifications/taxCertificationSlice';
import { getTaxCertificationRequestAsyncAction } from '../../../store/order/taxCertifications/reducer';
import {
  saveTaxCertificationsAsyncAction,
  saveTaxCertificationsSlice,
} from '../../../store/order/taxCertifications/saveTaxCertificationSlice';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { useDocumentTitleEffect } from '../../../utilities/hooks/useDocumentTitleEffect';
import { Copyright } from '../../../ui-toolkit/components/Copyright/Copyright';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { AccessControlCheck } from 'components/accessControl/AccessControlCheck';
import { eUSAState } from '../../../ui-toolkit/enums/states';
import { eAccessControlUserType } from 'ui-toolkit/enums/eAccessControlUserType';

export interface ITaxCertificationProps {
  orderId: string;
  userType: eAccessControlUserType;
}

export const TaxCertificationPage: React.FC<ITaxCertificationProps> = ({ orderId, userType }) => {
  const { fetchStatus: postFetch, fetchError: postError } = useSelector(
    (rootStore: RootStore) => rootStore.order.taxCertification.postTaxCertificationSlice
  );
  const { fetchStatus: saveFetch, fetchError: saveError } = useSelector(
    (rootStore: RootStore) => rootStore.order.taxCertification.saveTaxCertificationsSlice
  );

  const onResetSaveTaxCertifications = () => dispatch(saveTaxCertificationsSlice.actions.reset({}));
  const onResetPostTaxCertifications = () => dispatch(postTaxCertificationsSlice.actions.reset({}));

  const dispatch = useAppDispatch();
  useDocumentTitleEffect(`Tax Certification - Spruce`);

  useEffect(() => {
    dispatch(getTaxCertificationAsyncAction(orderId));
    dispatch(getTaxCertificationRequestAsyncAction(orderId));
  }, [dispatch, orderId]);

  const existingTaxCertRequest = useSelector(
    (state: RootStore) => state.order.taxCertification.taxCertificationRequestsSlice
  );

  const taxCertificationList = useSelector((state: RootStore) => state.order.taxCertification.taxCertificationSlice);
  const existingTaxCert =
    taxCertificationList.data.taxCertifications.length > 0 ? taxCertificationList.data.taxCertifications[0] : undefined;
  const existingPropertyState = existingTaxCert && existingTaxCert.propertyState;
  const countiesState = useSelector((state: RootStore) => state.counties);
  const onStateSelect = (state: eUSAState | null) => (state ? dispatch(getCountyForStateAction(state)) : {});

  useEffect(() => {
    existingPropertyState && dispatch(getCountyForStateAction(existingPropertyState as eUSAState));
  }, [existingPropertyState, dispatch]);

  const onSave = async (taxCertification: ITaxCertificationData) => {
    if (existingTaxCert && existingTaxCert.id) {
      await dispatch(
        saveTaxCertificationsAsyncAction({ orderId: orderId, taxCertification: prepForPut(taxCertification) })
      );
    } else {
      await dispatch(
        postTaxCertificationsAsyncAction({ orderId: orderId, taxCertification: prepForPost(taxCertification) })
      );
    }
  };

  const onCancel = () => {
    onResetPostTaxCertifications();
    onResetSaveTaxCertifications();
    if (userType === eAccessControlUserType.Admin) {
      window.location.href = ClientUrlFactory().urlFor.title(orderId);
    } else if (userType === eAccessControlUserType.Vendor && existingTaxCertRequest.data !== undefined) {
      window.location.href = ClientUrlFactory().urlFor.vendorTaxCertification(orderId);
    } else {
      // vendor user came from the title page, so redirect them back there
      window.history.back();
    }
  };

  const prepForPost = (taxCertification: ITaxCertificationData): ITaxCertificationData => {
    return {
      ...taxCertification,
      jurisdictions: taxCertification.jurisdictions.map((jur) => {
        const { id, ...jurNoId } = jur;
        return {
          ...jurNoId,
          taxYearInformation: jur.taxYearInformation.map((tyi) => {
            const { id, ...tyiNoId } = tyi;
            return tyiNoId;
          }),
        };
      }),
      taxCertificationDelinquencies: taxCertification.taxCertificationDelinquencies.map((del) => {
        const { id, ...delNoId } = del;
        return delNoId;
      }),
    };
  };

  const prepForPut = (taxCertification: ITaxCertificationData): ITaxCertificationData => {
    const { jurisdictions, taxCertificationDelinquencies: delinquencies } = taxCertification;
    return {
      ...taxCertification,
      jurisdictions: jurisdictions.map((jur) => ({
        ...jur,
        taxYearInformation: jur.taxYearInformation.map((tyi) => ({
          ...tyi,
          id: tyi.createdAt ? tyi.id : undefined,
        })),
        id: jur.createdAt ? jur.id : undefined,
      })),
      taxCertificationDelinquencies: delinquencies.map((del) => ({
        ...del,
        id: del.createdAt ? del.id : undefined,
      })),
    };
  };

  return (
    <>
      <AccessControlCheck allowedPermissions={[ePermissions.AdminRead]}>
        <StickyNavigation activeMain={'Orders'} profile={<UserProfileContainer />} />
      </AccessControlCheck>
      <AccessControlCheck allowedPermissions={[ePermissions.ReadVendorPortal]}>
        <StickyNavigation activeMain={'Orders'} />
      </AccessControlCheck>
      <ErrorMessage error={postError} />
      {postFetch.fetching && (
        <Card>
          <FlexRow>
            <Spinner size={SpinnerSize.regular} />
          </FlexRow>
        </Card>
      )}
      <SpaceWrapper>
        <Card title={'Tax Certification'}>
          <TaxCertificationForm
            onSave={onSave}
            onCancel={onCancel}
            existingTaxCertData={existingTaxCert || defaultTaxCertificationData}
            countiesState={countiesState}
            onStateSelect={onStateSelect}
          />
          <SwitchDialogWindows
            isCompleted={postFetch.fetched || saveFetch.fetched}
            isError={!!postError || !!saveError}
            isLoading={postFetch.fetching || saveFetch.fetching}
            onCloseClick={onCancel}
            loadingMessage={'Working...'}
            successTitle={'Tax Certification has been successfully saved!'}
            successMessage={
              <div>
                <BodyText>You will be redirected.</BodyText>
              </div>
            }
            errorMessage={'Tax Certification cannot be saved. Contact Spruce about this issue.'}
          />
        </Card>
      </SpaceWrapper>
      <Copyright />
    </>
  );
};
